import React from 'react';

import { usePartnerClientManagerProcess } from 'hooks/usePartnerClientManagerProcess';
import {
  SidebarModalSplitComponent,
  SidebarModalBlockComponent,
} from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import {
  SidebarModalNavigationControlComponent,
  SidebarModalNavigationLayoutComponent,
} from 'design/sidebarModalNavigation';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { useAppState } from 'hooks/useAppState';
import { TextInputControlComponent } from 'design/textInputControl';
import { useServices } from 'hooks/useServices';
import { useSafeAsyncWrapper } from 'hooks/useSafeAsyncWrapper';
import { PartnerClientManagerSummary } from './PartnerClientManagerSummary';
import { PartnerClientManagerPayments } from './PartnerClientManagerPayments';
import { NotesInputWrapperComponent } from 'design/notesInputWrapper/notesInputWrapperComponents';

type Screen = 'SUMMARY' | 'PAYMENTS';

export const PartnerClientManagerContent = () => {
  usePartnerClientManagerProcess();

  const wrapAsyncSafely = useSafeAsyncWrapper();

  const { setPartnerClientNotes } = useServices();

  const { partnerClientManager: ownDictionary } = useIntlDictionary();

  const dispatch = useAppDispatcher();

  const {
    partnerClientManager: {
      client,
      clientId,
      notesText,
      monthReports,
      organizationName,
      clientInvalidated,
    },
  } = useAppState();

  const [screen, setScreen] = React.useState<Screen>('SUMMARY');

  const viewSummary = React.useCallback(() => {
    setScreen('SUMMARY');
  }, []);

  const viewPayments = React.useCallback(() => {
    setScreen('PAYMENTS');
  }, []);

  React.useLayoutEffect(() => {
    if (clientInvalidated === false && client === null) {
      viewPayments();
    }
  }, [client, clientInvalidated, viewPayments]);

  const handleNotesChange = React.useCallback(
    (nextValue: string) => {
      dispatch({
        name: 'PartnerClientManager__NotesTextChanged',
        nextValue,
      });
    },
    [dispatch]
  );

  const submitNotes = React.useCallback(() => {
    if (notesText !== null) {
      const promise = setPartnerClientNotes({ clientId, notesText });

      wrapAsyncSafely(promise);
    }
  }, [clientId, notesText, setPartnerClientNotes, wrapAsyncSafely]);

  return (
    <SidebarModalSplitComponent
      left={
        <>
          <SidebarModalBlockComponent withBorder withoutBottomPadding>
            <TextComponent size="mpp" weight="medium">
              {organizationName}
            </TextComponent>
            <MarginComponent top={40} />
            <SidebarModalNavigationLayoutComponent>
              {client !== null && (
                <SidebarModalNavigationControlComponent
                  onPress={viewSummary}
                  active={screen === 'SUMMARY'}
                >
                  <TextComponent>{ownDictionary.summaryTabLabel}</TextComponent>
                </SidebarModalNavigationControlComponent>
              )}
              <SidebarModalNavigationControlComponent
                onPress={viewPayments}
                active={screen === 'PAYMENTS'}
              >
                <TextComponent>{ownDictionary.paymentsTabLabel}</TextComponent>
              </SidebarModalNavigationControlComponent>
            </SidebarModalNavigationLayoutComponent>
          </SidebarModalBlockComponent>
          {screen === 'SUMMARY' && client && (
            <PartnerClientManagerSummary client={client} />
          )}
          {screen === 'PAYMENTS' && (
            <PartnerClientManagerPayments monthReports={monthReports} />
          )}
        </>
      }
      right={
        notesText !== null && (
          <SidebarModalBlockComponent>
            <TextComponent size="mpp" weight="medium">
              {ownDictionary.notesTitle}
            </TextComponent>
            <MarginComponent top={25} />
            <NotesInputWrapperComponent>
              <TextInputControlComponent
                placeholder={ownDictionary.notesInputPlaceholder}
                value={notesText}
                multiLine
                hidePlaceholderOnFocus
                onValueChange={handleNotesChange}
                onBlur={submitNotes}
              />
            </NotesInputWrapperComponent>
          </SidebarModalBlockComponent>
        )
      }
    />
  );
};
